import { FC, useEffect, useState } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { AppFormQuestionVariant, APP_FORM_QUESTION_VARIANTS } from '../../../constants/featureFlags'
import { Label, P3 } from '../../../shared/compass-ui'
import FadeAndCollapse from '../../../shared/components/FadeAndCollapse'
import AccountsRadioInput from '../../shared/AccountsRadioInput'
import CashAdvanceRadioInput from '../../shared/CashAdvanceRadioInput'
import ControlledSelect from '../../shared/ControlledSelect'
import HiddenFieldMirror from '../../shared/HiddenFieldMirror'
import HomeRadioInput from '../../shared/HomeRadioInput'
import MoneyInput from '../../shared/MoneyInput'
import TextInputError from '../../shared/TextInputError'
import {
  AccountBalance,
  BnplDetails,
  CreditRepair,
  EducationBackground,
  EmploymentDetails,
  JobCountAndIncome,
  RecentAccountsOpened,
  RecentApplications,
  SelfEmploymentDetails,
  StudentDetails,
  UnemploymentDetails,
} from './components'

const EMPLOYMENT_STATUS_OPTIONS = [
  { label: 'Student', value: 'student' },
  { label: 'Employed', value: 'employed' },
  { label: 'Self-Employed', value: 'self_employed' },
  { label: 'Unemployed', value: 'unemployed' },
  { label: 'Retired', value: 'retired' },
]

interface FinancialInformationProps {
  appFormQuestionVariant?: AppFormQuestionVariant
}

const FinancialInformation: FC<FinancialInformationProps> = ({
  appFormQuestionVariant = APP_FORM_QUESTION_VARIANTS.CONTROL,
}) => {
  const [showHelperText, setShowHelperText] = useState<boolean>(false)
  const accountsValue = useWatch({ name: 'accounts' })
  const employmentStatusValue = useWatch({ name: 'employmentStatus' })
  const showAccountBalance =
    !!accountsValue &&
    accountsValue !== 'neither' &&
    appFormQuestionVariant === APP_FORM_QUESTION_VARIANTS.VARIANT_A
  const showEmploymentDetails =
    employmentStatusValue === 'employed' &&
    appFormQuestionVariant === APP_FORM_QUESTION_VARIANTS.VARIANT_B
  const showSelfEmploymentDetails =
    employmentStatusValue === 'self_employed' &&
    appFormQuestionVariant === APP_FORM_QUESTION_VARIANTS.VARIANT_B
  const showUnemploymentDetails =
    employmentStatusValue === 'unemployed' &&
    appFormQuestionVariant === APP_FORM_QUESTION_VARIANTS.VARIANT_B
  const showStudentDetails =
    employmentStatusValue === 'student' &&
    appFormQuestionVariant === APP_FORM_QUESTION_VARIANTS.VARIANT_B
  const showJobCountAndIncome = appFormQuestionVariant === APP_FORM_QUESTION_VARIANTS.VARIANT_C
  const showBnplDetails = appFormQuestionVariant === APP_FORM_QUESTION_VARIANTS.VARIANT_D
  const showRecentApplications = appFormQuestionVariant === APP_FORM_QUESTION_VARIANTS.VARIANT_E
  const showRecentAccountsOpened = appFormQuestionVariant === APP_FORM_QUESTION_VARIANTS.VARIANT_F
  const showCreditRepair = appFormQuestionVariant === APP_FORM_QUESTION_VARIANTS.VARIANT_G
  const showEducationBackground = appFormQuestionVariant === APP_FORM_QUESTION_VARIANTS.VARIANT_H
  const {
    formState: {
      errors: {
        payment: paymentError,
        employmentStatus: employmentStatusError,
        totalAnnualIncome: totalAnnualIncomeError,
        cashAdvance: cashAdvanceError,
        accounts: accountsError,
      },
    },
    clearErrors,
    unregister,
  } = useFormContext()

  useEffect(() => {
    if (!showAccountBalance) {
      unregister('totalAccountBalance')
    }
  }, [showAccountBalance, unregister])

  useEffect(() => {
    if (!showEmploymentDetails) {
      unregister(['jobRole', 'paymentMethod', 'timeAtCurrentJob'])
    }
  }, [showEmploymentDetails, unregister])

  useEffect(() => {
    if (!showSelfEmploymentDetails) {
      unregister(['industry', 'selfEmploymentDuration', 'workType'])
    }
  }, [showSelfEmploymentDetails, unregister])

  useEffect(() => {
    if (!showUnemploymentDetails) {
      unregister('unemploymentDuration')
    }
  }, [showUnemploymentDetails, unregister])

  useEffect(() => {
    if (!showStudentDetails) {
      unregister(['degreeType', 'institution', 'fieldOfStudy'])
    }
  }, [showStudentDetails, unregister])

  useEffect(() => {
    if (!showJobCountAndIncome) {
      unregister(['jobCount', 'additionalIncomeSources'])
    }
  }, [showJobCountAndIncome, unregister])

  useEffect(() => {
    if (!showBnplDetails) {
      unregister(['bnplFrequency', 'bnplPurchaseType', 'bnplLatePayments'])
    }
  }, [showBnplDetails, unregister])

  useEffect(() => {
    if (!showRecentApplications) {
      unregister('recentApplications')
    }
  }, [showRecentApplications, unregister])

  useEffect(() => {
    if (!showRecentAccountsOpened) {
      unregister('recentAccountsOpened')
    }
  }, [showRecentAccountsOpened, unregister])

  useEffect(() => {
    if (!showCreditRepair) {
      unregister('creditRepair')
    }
  }, [showCreditRepair, unregister])

  useEffect(() => {
    if (!showEducationBackground) {
      unregister(['educationLevel', 'educationFieldOfStudy'])
    }
  }, [showEducationBackground, unregister])

  return (
    <div
      className="grid grid-cols-4 md:grid-cols-6 gap-6"
      data-testid="financial-information-section"
    >
      <div className="col-span-full md:col-span-3">
        <HomeRadioInput />
      </div>
      <div className="col-span-full md:col-span-3">
        <Label htmlFor="monthly-payment-input">Monthly Rent / Mortgage Payment</Label>
        <MoneyInput
          name="payment"
          className={`border-2 font-normal p-4 pr-16 rounded w-full pl-8 ${
            typeof paymentError?.message === 'string'
              ? 'border-red-600 outline-red-600 focus:border-red-600 bg-[url(/assets/dollar.svg),_url(/assets/error.svg)] bg-no-repeat bg-[position:left_16px_center,_right_16px_center]'
              : 'border-ink-400 focus:border-blue-600 outline-blue-600 bg-dollar bg-no-repeat bg-[left_16px_center]'
          }`}
          error={typeof paymentError?.message === 'string'}
          suffix="per month"
          id="monthly-payment-input"
          maxLength={7}
        />
        {typeof paymentError?.message === 'string' && (
          <TextInputError>{paymentError.message}</TextInputError>
        )}
      </div>
      <div className="col-span-full md:col-span-3">
        <ControlledSelect
          id="employment-status-select"
          name="employmentStatus"
          label="Employment Status"
          options={EMPLOYMENT_STATUS_OPTIONS}
          error={typeof employmentStatusError?.message === 'string'}
          errorId="employmentStatus-error"
          registerOptions={{
            onChange: () => {
              if (employmentStatusError) {
                clearErrors('employmentStatus')
              }
            },
          }}
        />
        <HiddenFieldMirror name="employmentStatus" />
        {typeof employmentStatusError?.message === 'string' && (
          <TextInputError id="employmentStatus-error">This field is required</TextInputError>
        )}
      </div>
      {showJobCountAndIncome && <JobCountAndIncome />}
      <div className="col-span-full md:col-span-3">
        <Label htmlFor="total-annual-income-input">Total Annual Income</Label>
        <MoneyInput
          name="totalAnnualIncome"
          className={`border-2 font-normal p-4 pr-xl rounded w-full pl-8 ${
            typeof totalAnnualIncomeError?.message === 'string'
              ? 'border-red-600 outline-red-600 focus:border-red-600 bg-[url(/assets/dollar.svg),_url(/assets/error.svg)] bg-no-repeat bg-[position:left_16px_center,_right_16px_center]'
              : 'border-ink-400 focus:border-blue-600 outline-blue-600 bg-dollar bg-no-repeat bg-[left_16px_center]'
          }`}
          error={typeof totalAnnualIncomeError?.message === 'string'}
          suffix="per year"
          id="total-annual-income-input"
          maxLength={8}
          onBlur={() => setShowHelperText(false)}
          onFocus={() => setShowHelperText(true)}
        />
        {typeof totalAnnualIncomeError?.message === 'string' && (
          <TextInputError>{totalAnnualIncomeError.message}</TextInputError>
        )}
        <FadeAndCollapse show={showHelperText}>
          <P3 className="mt-2">
            If you&apos;re <span className="font-semibold text-ink-800">under 21</span>, only
            include your own income.
          </P3>
          <P3 className="mt-4">
            If you&apos;re <span className="font-semibold text-ink-800">21 or older</span>, you can
            include another person&apos;s income that is available to you if you regularly use that
            income to pay your expenses.
          </P3>
          <P3 className="mt-4">
            You don&apos;t have to include alimony, child support, or separate maintenance income.
            Only include this if you want us to consider it as a basis for repaying your credit.
          </P3>
        </FadeAndCollapse>
      </div>
      {showEducationBackground && <EducationBackground />}
      {showEmploymentDetails && <EmploymentDetails />}
      {showSelfEmploymentDetails && <SelfEmploymentDetails />}
      {showUnemploymentDetails && <UnemploymentDetails />}
      {showStudentDetails && <StudentDetails />}
      <div className="col-span-full md:col-span-3">
        <Label
          id="bank-accounts-radio-group"
          data-testid="bank-accounts-radio-group"
          htmlFor="accounts-radio-inputs-group"
        >
          Which types of bank accounts do you have?
        </Label>
        <AccountsRadioInput />
        {accountsError && <TextInputError>This field is required</TextInputError>}
        {showAccountBalance && <AccountBalance />}
      </div>
      <div className="col-span-full md:col-span-3">
        <Label
          data-testid="cash-advance-question-radio-group"
          id="cash-advance-question-radio-group"
          htmlFor="cash-advance-radio-inputs-group"
        >
          If approved, would you be interested in using this card for cash advances?
        </Label>
        <CashAdvanceRadioInput />
        {cashAdvanceError && <TextInputError>This field is required</TextInputError>}
      </div>
      {showBnplDetails && <BnplDetails />}
      {showRecentApplications && <RecentApplications />}
      {showRecentAccountsOpened && <RecentAccountsOpened />}
      {showCreditRepair && <CreditRepair />}
    </div>
  )
}
export default FinancialInformation
